import React, { useEffect, useState, lazy, Suspense } from "react";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LazyMotion, domAnimation } from "framer-motion";
import { CartProvider } from "./context/CartContext";

// Above-the-fold: load eagerly
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";

// Below-the-fold: split into separate chunks
const About = lazy(() => import("./components/About"));
const Menu = lazy(() => import("./components/Menu"));
const Gallery = lazy(() => import("./components/Gallery"));
const Reviews = lazy(() => import("./components/Reviews"));
const Location = lazy(() => import("./components/Location"));
const FAQ = lazy(() => import("./components/FAQ"));
const Contact = lazy(() => import("./components/Contact"));
const Footer = lazy(() => import("./components/Footer"));
const FloatingButtons = lazy(() => import("./components/FloatingButtons"));
const StickyBottomNav = lazy(() => import("./components/StickyBottomNav"));
const CartDrawer = lazy(() => import("./components/CartDrawer"));
const StudentClubPopup = lazy(() => import("./components/StudentClubPopup"));
const MenuPage = lazy(() => import("./pages/MenuPage"));

const SCHEMA_RESTAURANT = {
  "@context": "https://schema.org",
  "@type": "Restaurant",
  name: "Himalayan Bowl & Juice Bar",
  alternateName: [
    "Himalayan Bowl",
    "Himalayan Bowl and Juice Bar",
    "Nepalese Indian Kitchen Gainesville",
  ],
  description:
    "Himalayan Bowl & Juice Bar is an authentic Nepalese and Indian restaurant in Gainesville, FL. We serve hand-folded Momo dumplings, Thukpa noodle soup, Chowmein, protein bowls, samosa, biryani, tandoori, cold-pressed juices, fresh smoothies, and Himalayan masala tea — all made from scratch daily.",
  url: "https://himalayanbowlandjuicebar.com",
  telephone: "+1-352-256-9540",
  email: "info@himalayanbowlandjuicebar.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: "150 NW 13th St Apt 10",
    addressLocality: "Gainesville",
    addressRegion: "FL",
    postalCode: "32608",
    addressCountry: "US",
  },
  geo: { "@type": "GeoCoordinates", latitude: 29.6173, longitude: -82.3883 },
  hasMap:
    "https://www.google.com/maps/dir//Himalayan+Bowl+%26+Juice+Bar,+150+NW+13th+St+Apt+10,+Gainesville,+FL+32601,+United+States/@27.6704274,85.3239595,15z/data=!4m8!4m7!1m0!1m5!1m1!1s0x88e8a5775e96bfe1:0xa286a46d4bab2623!2m2!1d-82.3394014!2d29.6531473?entry=ttu&g_ep=EgoyMDI2MDgxMi4wIKXMDSoASAFQAw%3D%3D",
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday"],
      opens: "11:00",
      closes: "22:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Friday", "Saturday"],
      opens: "11:00",
      closes: "23:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: "Sunday",
      opens: "12:00",
      closes: "21:00",
    },
  ],
  servesCuisine: [
    "Nepali",
    "Indian",
    "Himalayan",
    "South Asian",
    "Tibetan-influenced",
    "Vegetarian-friendly",
  ],
  priceRange: "$$",
  currenciesAccepted: "USD",
  paymentAccepted: "Cash, Credit Card, Debit Card",
  image: [
    "https://himalayanbowlandjuicebar.com/restaurant.webp",
    "https://himalayanbowlandjuicebar.com/images/hero-1.webp",
    "https://himalayanbowlandjuicebar.com/images/hero-2.webp",
  ],
  logo: "https://himalayanbowlandjuicebar.com/logo.png",
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "153",
    bestRating: "5",
    worstRating: "1",
  },
  menu: "https://himalayanbowlandjuicebar.com/#menu",
  amenityFeature: [
    {
      "@type": "LocationFeatureSpecification",
      name: "Free Parking",
      value: true,
    },
    { "@type": "LocationFeatureSpecification", name: "Takeout", value: true },
    { "@type": "LocationFeatureSpecification", name: "Dine-in", value: true },
    { "@type": "LocationFeatureSpecification", name: "Catering", value: true },
    {
      "@type": "LocationFeatureSpecification",
      name: "Vegetarian Options",
      value: true,
    },
    {
      "@type": "LocationFeatureSpecification",
      name: "Vegan Options",
      value: true,
    },
    {
      "@type": "LocationFeatureSpecification",
      name: "Gluten-Free Options",
      value: true,
    },
  ],
  sameAs: [
    "https://www.facebook.com/profile.php?id=61575444158652",
    "https://www.instagram.com/himalayanbowljuice//",
    "https://www.tiktok.com/@himalayan.bowl.ju",
  ],
  areaServed: {
    "@type": "City",
    name: "Gainesville",
    addressRegion: "FL",
    addressCountry: "US",
  },
  foundingDate: "2026",
  keywords:
    "momo, Nepali food, Indian food Gainesville, thukpa, chowmein, protein bowl, cold pressed juice, Himalayan restaurant, Nepalese restaurant",
};

const SCHEMA_MENU = {
  "@context": "https://schema.org",
  "@type": "Menu",
  name: "Himalayan Bowl & Juice Bar Menu",
  url: "https://himalayanbowlandjuicebar.com/#menu",
  inLanguage: "en",
  hasMenuSection: [
    {
      "@type": "MenuSection",
      name: "Himalayan Momo Bowls",
      description:
        "Hand-folded Nepali dumplings — Tibetan in origin, perfected in Kathmandu — served over basmati rice with signature sauces.",
      hasMenuItem: [
        {
          "@type": "MenuItem",
          name: "Momo Bowl — Veg / Chicken",
          description:
            "Dumplings stuffed with Chicken or Veg mixed with cabbage, garlic, and home-made spices. Styles: Pan Fried, Crunchy, Kothey, Sweet & Spicy.",
          offers: { "@type": "Offer", price: "10.99", priceCurrency: "USD" },
        },
      ],
    },
    {
      "@type": "MenuSection",
      name: "Himalayan Protein Bowls",
      description:
        "Choice of protein over basmati rice with Himalayan green salad, Himalayan special sauces, and toppings.",
      hasMenuItem: [
        {
          "@type": "MenuItem",
          name: "Regular Protein Bowl",
          description:
            "Choice of 1 protein + 1 Himalayan Special Sauce on basmati rice with green salad.",
          offers: { "@type": "Offer", price: "10.99", priceCurrency: "USD" },
        },
        {
          "@type": "MenuItem",
          name: "Large Protein Bowl",
          description:
            "Choice of 2 proteins + 2 Himalayan Special Sauces on basmati rice with green salad.",
          offers: { "@type": "Offer", price: "13.99", priceCurrency: "USD" },
        },
      ],
    },
    {
      "@type": "MenuSection",
      name: "Noodles",
      hasMenuItem: [
        {
          "@type": "MenuItem",
          name: "Chowmein — Veg / Chicken",
          description:
            "Stir-fried noodles with fresh vegetables and your choice of protein.",
          offers: { "@type": "Offer", price: "13.99", priceCurrency: "USD" },
        },
        {
          "@type": "MenuItem",
          name: "Thukpa — Veg / Chicken",
          description:
            "Comforting Himalayan noodle soup with tender noodles and aromatic spices.",
          offers: { "@type": "Offer", price: "13.99", priceCurrency: "USD" },
        },
      ],
    },
    {
      "@type": "MenuSection",
      name: "Cold Pressed Juice",
      description: "Naturally cold-pressed juices to energize your body.",
      hasMenuItem: [
        {
          "@type": "MenuItem",
          name: "Bright Skin",
          description: "Carrot + Orange + Turmeric + Lemon.",
          offers: { "@type": "Offer", price: "8.99", priceCurrency: "USD" },
        },
        {
          "@type": "MenuItem",
          name: "Immunity Boost",
          description: "Orange + Carrot + Apple + Ginger + Turmeric.",
          offers: { "@type": "Offer", price: "8.99", priceCurrency: "USD" },
        },
        {
          "@type": "MenuItem",
          name: "Green Energy",
          description: "Green Apple + Spinach + Cucumber + Lemon.",
          offers: { "@type": "Offer", price: "8.99", priceCurrency: "USD" },
        },
      ],
    },
    {
      "@type": "MenuSection",
      name: "Fresh Smoothies",
      hasMenuItem: [
        {
          "@type": "MenuItem",
          name: "Energy Power",
          description: "Banana + Mango + Syrup smoothie.",
          offers: { "@type": "Offer", price: "5.99", priceCurrency: "USD" },
        },
        {
          "@type": "MenuItem",
          name: "Protein Shake",
          description: "Protein Powder + Banana + Milk.",
          offers: { "@type": "Offer", price: "5.99", priceCurrency: "USD" },
        },
      ],
    },
    {
      "@type": "MenuSection",
      name: "Appetizers",
      hasMenuItem: [
        {
          "@type": "MenuItem",
          name: "Samosa — Veg / Chicken",
          description:
            "Crispy golden pastry filled with seasoned meat or vegetables.",
          offers: { "@type": "Offer", price: "4.99", priceCurrency: "USD" },
        },
        {
          "@type": "MenuItem",
          name: "Veg Pakoda",
          description:
            "Mixed vegetables in seasoned batter, deep-fried until crispy.",
          offers: { "@type": "Offer", price: "7.99", priceCurrency: "USD" },
        },
      ],
    },
  ],
};

const SCHEMA_LOCAL_BUSINESS = {
  "@context": "https://schema.org",
  "@type": ["FoodEstablishment", "LocalBusiness"],
  name: "Himalayan Bowl & Juice Bar",
  image: "https://himalayanbowlandjuicebar.com/restaurant.webp",
  url: "https://himalayanbowlandjuicebar.com",
  telephone: "+1-352-256-9540",
  address: {
    "@type": "PostalAddress",
    streetAddress: "150 NW 13th St Apt 10",
    addressLocality: "Gainesville",
    addressRegion: "FL",
    postalCode: "32608",
    addressCountry: "US",
  },
  geo: { "@type": "GeoCoordinates", latitude: 29.6173, longitude: -82.3883 },
  openingHours: ["Mo-Th 11:00-22:00", "Fr-Sa 11:00-23:00", "Su 12:00-21:00"],
  priceRange: "$$",
  servesCuisine: "Nepali, Indian, Himalayan",
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "153",
  },
};

const SCHEMA_BREADCRUMB = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://himalayanbowlandjuicebar.com",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Menu",
      item: "https://himalayanbowlandjuicebar.com/#menu",
    },
    {
      "@type": "ListItem",
      position: 3,
      name: "Gallery",
      item: "https://himalayanbowlandjuicebar.com/#gallery",
    },
    {
      "@type": "ListItem",
      position: 4,
      name: "Location",
      item: "https://himalayanbowlandjuicebar.com/#location",
    },
  ],
};

const SCHEMA_WEBPAGE = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Himalayan Bowl & Juice Bar — Nepalese & Indian Restaurant Gainesville FL",
  url: "https://himalayanbowlandjuicebar.com",
  description:
    "Himalayan Bowl & Juice Bar serves authentic Nepali and Indian food in Gainesville, FL. Momo, Thukpa, Protein Bowls, cold-pressed juices, and more — made from scratch daily.",
  inLanguage: "en-US",
  isPartOf: {
    "@type": "WebSite",
    name: "Himalayan Bowl & Juice Bar",
    url: "https://himalayanbowlandjuicebar.com",
  },
  about: { "@type": "Restaurant", name: "Himalayan Bowl & Juice Bar" },
  dateModified: "2026-08-03",
};

export default function App() {
  const [popupOpen, setPopupOpen] = useState(true);
  const [popupInstant, setPopupInstant] = useState(false);

  function openPopup(instant = false) {
    setPopupInstant(instant);
    setPopupOpen(true);
  }

  return (
    <HelmetProvider>
      <LazyMotion features={domAnimation} strict>
        <CartProvider>
          <BrowserRouter>
            <Suspense fallback={null}>
              <Routes>
                <Route path="/menu" element={<MenuPage />} />
                <Route
                  path="/*"
                  element={<HomePage onStudentOffer={() => openPopup(true)} />}
                />
              </Routes>
              {popupOpen && (
                <StudentClubPopup
                  instant={popupInstant}
                  onClose={() => setPopupOpen(false)}
                />
              )}
            </Suspense>
          </BrowserRouter>
        </CartProvider>
      </LazyMotion>
    </HelmetProvider>
  );
}

function HomePage({ onStudentOffer }) {
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    let lenis;
    const id = requestAnimationFrame(() => {
      import("lenis").then(({ default: Lenis }) => {
        lenis = new Lenis({
          duration: 1.2,
          easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
          smoothWheel: true,
          autoRaf: true,
        });
      });
    });
    return () => {
      cancelAnimationFrame(id);
      lenis?.destroy();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>
          Himalayan Bowl & Juice Bar | Nepalese & Indian Restaurant –
          Gainesville, FL
        </title>
        <meta
          name="description"
          content="Himalayan Bowl & Juice Bar — authentic Nepalese & Indian restaurant in Gainesville, FL. Hand-folded Momo, Thukpa, Chowmein, protein bowls, cold-pressed juices & more. Open daily. Free parking. Dine-in & takeout."
        />
        <meta
          name="keywords"
          content="Nepali restaurant Gainesville FL, Indian restaurant Gainesville, momo Gainesville, Himalayan food Gainesville, thukpa, chowmein, protein bowl, cold pressed juice bar, Nepalese food near me, best Indian food Gainesville"
        />
        <link rel="canonical" href="https://himalayanbowlandjuicebar.com/" />

        {/* Geo tags for local SEO */}
        <meta name="geo.region" content="US-FL" />
        <meta name="geo.placename" content="Gainesville, Florida" />
        <meta name="geo.position" content="29.6173;-82.3883" />
        <meta name="ICBM" content="29.6173, -82.3883" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Himalayan Bowl & Juice Bar | Nepalese & Indian Restaurant – Gainesville, FL"
        />
        <meta
          property="og:description"
          content="Authentic Nepalese & Indian food in Gainesville, FL. Hand-folded Momo,  Thukpa, protein bowls, cold-pressed juices & more. Made from scratch daily."
        />
        <meta
          property="og:url"
          content="https://himalayanbowlandjuicebar.com/"
        />
        <meta
          property="og:image"
          content="https://himalayanbowlandjuicebar.com/restaurant.webp"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content="Himalayan Bowl & Juice Bar restaurant in Gainesville FL"
        />
        <meta property="og:site_name" content="Himalayan Bowl & Juice Bar" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Himalayan Bowl & Juice Bar | Nepalese & Indian Restaurant – Gainesville, FL"
        />
        <meta
          name="twitter:description"
          content="Authentic Nepalese & Indian food in Gainesville, FL. Hand-folded Momo, Thukpa, protein bowls, cold-pressed juices & more."
        />
        <meta
          name="twitter:image"
          content="https://himalayanbowlandjuicebar.com/restaurant.webp"
        />
        <meta
          name="twitter:image:alt"
          content="Himalayan Bowl & Juice Bar — Gainesville FL"
        />

        {/* Security headers via meta */}
        <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
        <meta httpEquiv="X-Frame-Options" content="DENY" />
        <meta name="referrer" content="strict-origin-when-cross-origin" />

        <meta
          name="robots"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
        <meta name="theme-color" content="#0F2260" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, viewport-fit=cover"
        />

        {/* Structured data */}
        <script type="application/ld+json">
          {JSON.stringify(SCHEMA_RESTAURANT)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(SCHEMA_LOCAL_BUSINESS)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(SCHEMA_MENU)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(SCHEMA_BREADCRUMB)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(SCHEMA_WEBPAGE)}
        </script>
      </Helmet>

      {/* Skip nav */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[9999] focus:bg-navy-dark focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:font-bold focus:text-sm"
      >
        Skip to main content
      </a>

      <Navbar onStudentOffer={onStudentOffer} />

      <main id="main-content">
        <Hero />
        <Suspense fallback={null}>
          <About />
          <Menu onOpenCart={() => setCartOpen(true)} />
          <Gallery />
          <Reviews />
          <Location />
          <FAQ />
          <Contact />
        </Suspense>
      </main>

      <Suspense fallback={null}>
        <Footer />
        <FloatingButtons onOpenCart={() => setCartOpen(true)} />
        <StickyBottomNav />
        <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
      </Suspense>
    </>
  );
}
